import { useAuth0 } from "@auth0/auth0-react";
import { accessTokenSignal } from "@src/auth/auth-utils";
import { shouldUseDesktopBrowserLogin } from "@src/auth/desktop-browser-login";
import { CARD_AUTORESIZE_STORAGE_KEY } from "@src/components/cards/hooks/use-card-auto-resize";
import { PREPRINT_SELECTED_VERSION_STORAGE_KEY } from "@src/components/cards/preprint-card/hooks/preprint-card-selected-version-store";
import { clearAgentDefaultPreferenceOnLogout } from "@src/components/cards/prompt-card/hooks/use-agent-default-preference-store";
import { AGENT_MODEL_STORAGE_KEY } from "@src/components/cards/prompt-card/hooks/use-agent-model-store";
import { ONBOARDING_DEMO_STORAGE_KEY } from "@src/components/demo-board/onboarding-demo-store";
import { clearDemoRegistrationContext } from "@src/components/demo-board/onboarding-demo-store";
import { clearCreateBoardModalStorageOnLogout } from "@src/components/modals/create-board/create-board-modal-store";
import { UPGRADE_CARD_DISMISSED_KEY } from "@src/components/plan/constants";
import config from "@src/config";
import { clearReferralCode } from "@src/utils/referral-tracker";
import { clearReferralWelcomeStorageOnLogout } from "@src/utils/referral-welcome-storage";
import { clearUtmParams } from "@src/utils/utm-tracker";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";

type GAWindow = Window & {
	gtag?: (
		command: "config" | "set",
		targetOrParams: string | Record<string, unknown>,
		params?: Record<string, unknown>,
	) => void;
};

const SESSION_STORAGE_KEYS = [
	AGENT_MODEL_STORAGE_KEY,
	CARD_AUTORESIZE_STORAGE_KEY,
	ONBOARDING_DEMO_STORAGE_KEY,
	PREPRINT_SELECTED_VERSION_STORAGE_KEY,
	UPGRADE_CARD_DISMISSED_KEY,
] as const;

/**
 * Clears all session storage keys used by the app for per-session state.
 * Call on logout so the next user gets a clean slate.
 */
function clearSessionStorageOnLogout(): void {
	try {
		for (const key of SESSION_STORAGE_KEYS) {
			sessionStorage.removeItem(key);
		}
	} catch {
		// sessionStorage may be unavailable (e.g. private mode)
	}
}

/**
 * Clears localStorage keys that should not persist across users.
 */
function clearLocalStorageOnLogout(): void {
	try {
		localStorage.removeItem("cursorMessage");
		clearAgentDefaultPreferenceOnLogout();
		clearCreateBoardModalStorageOnLogout();
		clearDemoRegistrationContext();
		clearReferralCode();
		clearReferralWelcomeStorageOnLogout();
		clearUtmParams();
	} catch {
		// localStorage may be unavailable
	}
}

/**
 * Centralized logout: resets in-memory state, session/local storage,
 * analytics, then Auth0 session (redirect on web; local-only on desktop
 * browser-login — system browser is not opened).
 */
export function useLogout() {
	const { logout } = useAuth0();
	const posthog = usePostHog();

	return useCallback(() => {
		const gaWindow = window as GAWindow;

		clearSessionStorageOnLogout();
		clearLocalStorageOnLogout();
		if (config.googleAnalyticsId && gaWindow.gtag) {
			gaWindow.gtag("config", config.googleAnalyticsId, {
				user_id: undefined,
			});
			gaWindow.gtag("set", "user_properties", {
				email: undefined,
				name: undefined,
				display_name: undefined,
				first_name: undefined,
				last_name: undefined,
			});
		}
		posthog?.reset(true);
		accessTokenSignal.value = null;

		// Desktop browser-login: clear local SPA cache only. Do not open the
		// system browser — Auth0 /v2/logout there is a blank page and SSO in
		// Chrome is unrelated to Electron storage. Next Sign in uses
		// prompt=login + max_age=0 so Auth0 still shows the login UI.
		if (shouldUseDesktopBrowserLogin()) {
			void logout({ openUrl: false }).then(() => {
				window.location.assign(window.location.origin);
			});
			return;
		}

		logout({
			logoutParams: {
				returnTo: window.location.origin,
			},
		});
	}, [logout, posthog]);
}
